import type { FC } from 'react';
import { useEffect, useState } from 'react';

import CallReceivedIcon from '@mui/icons-material/CallReceived';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { Country, TvDevice } from 'src/api/zrm';
import { TvDashboard } from 'src/api/zrm';
import { COUNTRY_TIMEZONES, DashboardStatus } from 'src/tv/dashboardParts';
import { CallLayer } from 'src/tv/live/CallLayer';
import LiveFeed, { callEntry, FEED_LENGTH, visitEntry } from 'src/tv/live/LiveFeed';
import { MapFrame } from 'src/tv/live/MapFrame';
import type { Call } from 'src/tv/live/places';
import { useLiveFeed } from 'src/tv/live/useLiveFeed';
import type { Visit } from 'src/tv/live/useLiveFeed';
import { VISIT_LIFETIME_MS, VisitLayer } from 'src/tv/live/VisitLayer';
import TvFrame from 'src/tv/TvFrame';
import { TV_COLORS } from 'src/tv/tvTheme';
import { useTvDevice } from 'src/tv/useTvDevice';

import type { TFunction } from 'i18next';

const HOUR_MS = 60 * 60 * 1000;
// A visitor stays on the live map for 90 s; the web traffic screen uses VisitLayer's own lifetime.
const LIVE_MAP_VISIT_LIFETIME_MS = 90_000;
const LABEL_SX = { fontSize: '0.9vw', color: TV_COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.1em' };

export type LiveDashboard = TvDashboard.CallsMap | TvDashboard.WebTraffic | TvDashboard.LiveMap;

/** The live screens: zrm's socket for each, and which feeds it draws. */
export const LIVE_SCREENS: Record<LiveDashboard, { path: string; hasCalls: boolean; hasVisits: boolean }> = {
  [TvDashboard.CallsMap]: { path: '/tv/calls-map/live', hasCalls: true, hasVisits: false },
  [TvDashboard.WebTraffic]: { path: '/tv/web-traffic/live', hasCalls: false, hasVisits: true },
  [TvDashboard.LiveMap]: { path: '/tv/live-map/live', hasCalls: true, hasVisits: true },
};

export const isLiveDashboard = (dashboard: TvDashboard): dashboard is LiveDashboard => dashboard in LIVE_SCREENS;

// Literal keys, so `npm run save-translations` finds them.
const feedTitle = (dashboard: LiveDashboard, t: TFunction): string => {
  if (dashboard === TvDashboard.CallsMap) return t('Latest calls');

  if (dashboard === TvDashboard.WebTraffic) return t('Latest visitors');

  return t('Latest');
};

/** The last hour's identified and submitted visitors, by zrm's clock: a TV's own can be minutes off. */
const HourCounts: FC<{ visits: Visit[]; clockOffsetMs: number }> = (props) => {
  const { visits, clockOffsetMs } = props;
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 10_000);

    return () => clearInterval(timer);
  }, []);

  const lastHour = visits.filter((visit) => now + clockOffsetMs - visit.at < HOUR_MS);
  const counts = [
    { label: t('Identified · last hour'), value: lastHour.filter((visit) => visit.kind === 'identified').length },
    { label: t('Submitted · last hour'), value: lastHour.filter((visit) => visit.kind === 'submitted').length },
  ];

  return (
    <Box sx={{ display: 'flex', gap: '2vw' }}>
      {counts.map((count) => (
        <Box key={count.label}>
          <Typography sx={{ fontSize: '3.2vw', fontWeight: 700, lineHeight: 1 }}>{count.value}</Typography>
          <Typography sx={LABEL_SX}>{count.label}</Typography>
        </Box>
      ))}
    </Box>
  );
};

/** On the live map, which icon is a call and which a visitor; the map's colours say the rest. */
const Legend: FC = () => {
  const { t } = useTranslation();
  const items = [
    { label: t('Calls'), icon: <CallReceivedIcon sx={{ fontSize: '1.4vw' }} /> },
    { label: t('Web'), icon: <FingerprintIcon sx={{ fontSize: '1.4vw' }} /> },
  ];

  return (
    <Box sx={{ display: 'flex', gap: '2vw' }}>
      {items.map((item) => (
        <Box
          key={item.label}
          sx={{ display: 'flex', alignItems: 'center', gap: '0.6vw', fontSize: '1.3vw', fontWeight: 600 }}
        >
          {item.icon}
          {item.label}
        </Box>
      ))}
    </Box>
  );
};

interface LiveBoardProps {
  dashboard: LiveDashboard;
  calls: Call[];
  visits: Visit[];
  clockOffsetMs: number;
  country: Country;
}

/** The map and the list without the wall screen's frame: the TV screens and the agent `/live` page draw it. */
export const LiveBoard: FC<LiveBoardProps> = (props) => {
  const { dashboard, calls, visits, clockOffsetMs, country } = props;
  const { t } = useTranslation();
  const { hasCalls, hasVisits } = LIVE_SCREENS[dashboard];
  const timeZone = COUNTRY_TIMEZONES[country];
  // Blue alone, or white beside the blue calls.
  const visitColor = hasCalls ? TV_COLORS.text : TV_COLORS.accent;
  // Both lists are newest first, so the list needs only the head of each.
  const entries = [
    ...calls.slice(0, FEED_LENGTH).map((call) => callEntry(call, t)),
    ...visits.slice(0, FEED_LENGTH).map((visit) => visitEntry(visit, t)),
  ].sort((a, b) => b.at - a.at);

  let header = null;

  if (hasCalls && hasVisits) header = <Legend />;
  else if (hasVisits)
    header = (
      <HourCounts
        visits={visits}
        clockOffsetMs={clockOffsetMs}
      />
    );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 26vw', gridTemplateRows: 'minmax(0, 1fr)', gap: '2vw', mt: '2vh' }}>
      <Box sx={{ minHeight: 0, position: 'relative' }}>
        <MapFrame>
          {(now) => (
            <>
              {hasVisits && (
                <VisitLayer
                  visits={visits}
                  now={now}
                  color={visitColor}
                  lifetimeMs={dashboard === TvDashboard.LiveMap ? LIVE_MAP_VISIT_LIFETIME_MS : VISIT_LIFETIME_MS}
                />
              )}
              {hasCalls && (
                <CallLayer
                  calls={calls}
                  now={now}
                />
              )}
            </>
          )}
        </MapFrame>
        {/* Required by the DB-IP lite licence (CC BY 4.0). */}
        {hasVisits && (
          <Typography sx={{ position: 'absolute', left: 0, bottom: 0, fontSize: '0.7vw', color: TV_COLORS.muted, opacity: 0.6 }}>
            IP Geolocation by DB-IP
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3vh', minHeight: 0 }}>
        {header}
        <LiveFeed
          title={feedTitle(dashboard, t)}
          entries={entries}
          timeZone={timeZone}
          country={country}
        />
      </Box>
    </Box>
  );
};

interface LiveScreenProps {
  dashboard: LiveDashboard;
  token: string;
  /** Until `/tv/me` answers after a reload, the host's guess. */
  country: Country;
  device: TvDevice | null;
  onDevice: (device: TvDevice) => void;
  /** Called when zrm closes the socket as unpaired, or `/tv/me` answers 401; must be idempotent. */
  onUnpaired: () => void;
}

/**
 * `/calls-map`, `/web-traffic` and `/live-map`: calls as arcs from the offices, web visitors as pings on their town,
 * live over zrm's socket, with the latest of them in a list. `/tv/me` is checked like on the dashboards, for the
 * screen's country (its language and time zone).
 */
const LiveScreen: FC<LiveScreenProps> = (props) => {
  const { dashboard, token, country, device, onDevice, onUnpaired } = props;
  const { path } = LIVE_SCREENS[dashboard];
  const { calls, visits, offlineSince, clockOffsetMs } = useLiveFeed(dashboard, path, token, onUnpaired);
  const deviceOfflineSince = useTvDevice(dashboard, token, device, onDevice, onUnpaired);
  const outageSince = [offlineSince, deviceOfflineSince].filter((since): since is number => since !== null);

  return (
    <TvFrame
      isDense
      aside={(
        <DashboardStatus
          country={country}
          generatedAt={null}
          offlineSince={outageSince.length ? Math.min(...outageSince) : null}
        />
      )}
    >
      <LiveBoard
        dashboard={dashboard}
        calls={calls}
        visits={visits}
        clockOffsetMs={clockOffsetMs}
        country={country}
      />
    </TvFrame>
  );
};

export default LiveScreen;
